import type { RewardUnlock, UnlockKind } from './config';


export const COSMETICS_VERSION = 1;

export const MAX_PINNED_BADGES = 3;

export const COSMETIC_TIERS = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic', 'divine', 'senita'] as const;
export type CosmeticTier = (typeof COSMETIC_TIERS)[number];

export interface CosmeticDef {
  key: string;
  
  label: string;
  tier: CosmeticTier;
  
  secret?: true;
  paidOnly?: true;
}

function item<K extends string>(key: K, label: string, tier: CosmeticTier): CosmeticDef & { key: K } {
  return { key, label, tier };
}

function secretItem<K extends string>(key: K, label: string): CosmeticDef & { key: K } {
  return { key, label, tier: 'senita', secret: true };
}
function premiumItem<K extends string>(key: K, label: string): CosmeticDef & { key: K } {
  return { key, label, tier: 'legendary', paidOnly: true };
}


export const SENITA_COSMETICS = {
  title: 'senita_ascended',
  frame: 'senita_nebula',
  banner: 'senita_cosmos',
} as const;

export const TITLES = [
  premiumItem('premium_supporter', 'Gardien d’ANIDEX'),
  item('collector', 'Collectionneur', 'rare'),
  item('living_encyclopedia', 'Encyclopédie vivante', 'legendary'),
  item('mythic_ten', 'Panthéon mythique', 'mythic'),
  item('master_smith', 'Maître forgeron', 'epic'),
  item('booster_abyss', 'Abysse de boosters', 'legendary'),
  item('merchant', 'Marchand', 'rare'),
  item('deal_maker', 'Maître des échanges', 'epic'),
  item('elite', 'Élite', 'legendary'),
  item('social_hub', 'Carrefour social', 'rare'),
  item('page_turner', 'Tourneur de pages', 'uncommon'),
  item('lore_keeper', 'Gardien des récits', 'epic'),
  item('myth_weaver', 'Tisseur de mythes', 'mythic'),
  item('star_archivist', 'Archiviste des étoiles', 'legendary'),
  item('gold_tongue', 'Langue d’or', 'epic'),
  item('vault_baron', 'Baron du coffre', 'legendary'),
  item('auction_ghost', 'Fantôme des enchères', 'rare'),
  item('pact_binder', 'Lieur de pactes', 'epic'),
  item('anima_alchemist', 'Alchimiste d’Anima', 'legendary'),
  item('ember_hand', 'Main de braise', 'rare'),
  item('shard_saint', 'Saint des éclats', 'epic'),
  item('awakening_bell', 'Cloche de l’éveil', 'legendary'),
  item('forge_hymn', 'Hymne de la forge', 'mythic'),
  item('pack_storm', 'Tempête de pochettes', 'rare'),
  item('pity_breaker', 'Briseur de pitié', 'epic'),
  item('lucky_fool', 'Fou chanceux', 'legendary'),
  item('divine_touch', 'Doigt du destin', 'divine'),
  item('week_sentinel', 'Sentinelle de la semaine', 'uncommon'),
  item('dawn_regular', 'Habitué de l’aube', 'rare'),
  item('century_soul', 'Âme du siècle', 'mythic'),
  item('streak_comet', 'Comète fidèle', 'legendary'),
  item('friend_beacon', 'Phare des amis', 'uncommon'),
  item('salon_host', 'Hôte du salon', 'epic'),
  item('whisper_net', 'Filet de murmures', 'rare'),
  item('reaction_heart', 'Cœur réactif', 'uncommon'),
  item('neon_idol', 'Idole néon', 'epic'),
  item('glitch_prince', 'Prince du glitch', 'mythic'),
  item('sumi_poet', 'Poète à l’encre', 'rare'),
  item('pixel_hero', 'Héros pixel', 'uncommon'),
  item('frost_monarch', 'Monarque du givre', 'legendary'),
  item('gothic_relic', 'Relique gothique', 'epic'),
  item('terminal_root', 'Racine du terminal', 'rare'),
  item('stained_saint', 'Saint du vitrail', 'legendary'),
  item('stamp_official', 'Tampon officiel', 'uncommon'),
  item('manga_lead', 'Rôle principal', 'epic'),
  item('aurora_name', 'Nom d’aurore', 'divine'),
  secretItem('senita_ascended', 'Senita'),
] as const satisfies readonly CosmeticDef[];

export const BADGES = [
  item('archive_keeper', 'Gardien des archives', 'legendary'),
  item('legendary_ten', 'Cour des légendes', 'epic'),
  item('insolent_luck', 'Chance insolente', 'mythic'),
  item('first_divine', 'Toucher divin', 'divine'),
  item('spent_forge', 'Forge ardente', 'rare'),
  item('booster_storm', 'Tornade de cartes', 'epic'),
  item('echo_chamber', 'Chambre d’échos', 'rare'),
  item('market_mogul', 'Collectionneur du marché', 'epic'),
  item('quest_veteran', 'Vétéran des quêtes', 'epic'),
  item('streak_thirty', 'Mois fidèle', 'rare'),
  item('album_sprout', 'Pousse d’album', 'common'),
  item('series_lantern', 'Lanterne des séries', 'uncommon'),
  item('rare_spark', 'Étincelle rare', 'uncommon'),
  item('shard_chip', 'Éclat brut', 'common'),
  item('first_polish', 'Premier poli', 'common'),
  item('curious_eye', 'Œil curieux', 'common'),
  item('deja_vu_mark', 'Marque du déjà-vu', 'uncommon'),
  item('stall_bell', 'Cloche de l’étal', 'uncommon'),
  item('handshake', 'Poignée scellée', 'common'),
  item('novice_ribbon', 'Ruban novice', 'common'),
  item('hex_dice', 'Dé hexagonal', 'rare'),
  item('fox_mask', 'Masque kitsune', 'epic'),
  item('torii_gate', 'Torii', 'rare'),
  item('hourglass', 'Sablier', 'uncommon'),
  item('anchor', 'Ancre', 'rare'),
  item('comet', 'Comète', 'epic'),
  item('vial', 'Fiole', 'uncommon'),
  item('sword_shield', 'Épée et bouclier', 'epic'),
  item('moon_rabbit', 'Lapin de lune', 'rare'),
  item('koi', 'Koï', 'rare'),
  item('cherry_seal', 'Sceau de cerisier', 'uncommon'),
  item('oni_fang', 'Croc d’oni', 'epic'),
  item('shuriken', 'Shuriken', 'uncommon'),
  item('fan', 'Éventail', 'rare'),
  item('lantern', 'Lanterne', 'uncommon'),
  item('drum', 'Tambour', 'rare'),
  item('wave_crest', 'Crête de vague', 'epic'),
  item('crane', 'Grue', 'rare'),
  item('dragon_scale', 'Écaille de dragon', 'legendary'),
  item('phoenix', 'Phénix', 'mythic'),
  item('ink_stamp', 'Tampon d’encre', 'uncommon'),
  item('vinyl', 'Vinyle', 'rare'),
  item('cassette', 'Cassette', 'uncommon'),
  item('pixel_coin', 'Pièce pixel', 'common'),
  item('neon_bolt', 'Éclair néon', 'epic'),
  item('frost_flake', 'Flocon', 'rare'),
  item('ember_coal', 'Braise', 'uncommon'),
  item('vine', 'Liane', 'uncommon'),
  item('gear', 'Engrenage', 'rare'),
  item('crown_chip', 'Éclat de couronne', 'legendary'),
] as const satisfies readonly CosmeticDef[];

export const FRAMES = [
  premiumItem('premium_orbit', 'Orbite dorée'),
  item('crystal', 'Cadre cristallin', 'rare'),
  item('awakened', 'Cadre éveillé', 'epic'),
  item('gold_filigree', 'Filigrane d’or', 'legendary'),
  item('shimenawa', 'Shimenawa', 'rare'),
  item('thorns', 'Ronces', 'epic'),
  item('chains', 'Chaînes', 'rare'),
  item('rune_ring', 'Cercle runique', 'legendary'),
  item('dragon_scale', 'Écailles', 'mythic'),
  item('gears', 'Engrenages', 'rare'),
  item('nacre', 'Nacre divine', 'divine'),
  item('laurel', 'Laurier', 'uncommon'),
  item('barbed', 'Fil barbelé', 'epic'),
  item('halo_ring', 'Anneau de halo', 'legendary'),
  item('pixel_bezel', 'Cadre pixel', 'uncommon'),
  item('neon_tube', 'Tube néon', 'epic'),
  item('ink_brush', 'Coup de pinceau', 'rare'),
  item('frost_rim', 'Liseré de givre', 'rare'),
  item('flame_rim', 'Liseré de flamme', 'epic'),
  item('gothic_arch', 'Arc gothique', 'legendary'),
  item('circuit', 'Circuit', 'rare'),
  item('stained', 'Vitrail', 'epic'),
  item('stamp_edge', 'Bord de tampon', 'uncommon'),
  item('manga_panel', 'Case manga', 'rare'),
  item('aurora_ring', 'Anneau d’aurore', 'mythic'),
  item('obsidian', 'Obsidienne', 'epic'),
  item('sakura', 'Pétales', 'uncommon'),
  item('chainmail', 'Cotte de mailles', 'rare'),
  item('compass', 'Rose des vents', 'epic'),
  item('eclipse', 'Éclipse', 'legendary'),
  item('void_ring', 'Anneau du vide', 'divine'),
  secretItem('senita_nebula', 'Nébuleuse Senita'),
] as const satisfies readonly CosmeticDef[];

export const AVATAR_FX = [
  premiumItem('premium_stardust', 'Éclats d’étoile'),
  item('explorer', 'Boussole d’explorateur', 'rare'),
  item('petals', 'Pétales', 'uncommon'),
  item('frost', 'Givre', 'rare'),
  item('neko_ears', 'Oreilles de neko', 'epic'),
  item('horns', 'Cornes', 'epic'),
  item('halo', 'Halo', 'legendary'),
  item('wings', 'Ailes', 'legendary'),
  item('scanlines', 'Balayage CRT', 'uncommon'),
  item('glitch', 'Glitch', 'epic'),
  item('kitsune', 'Kitsune', 'mythic'),
  item('summoning', 'Cercle d’invocation', 'legendary'),
  item('hud_reticle', 'Viseur', 'rare'),
  item('sparkles', 'Étincelles', 'common'),
  item('rain', 'Pluie', 'uncommon'),
  item('embers', 'Braises', 'rare'),
  item('bubbles', 'Bulles', 'common'),
  item('vines', 'Lianes', 'uncommon'),
  item('static', 'Neige analogique', 'rare'),
  item('moonlight', 'Clair de lune', 'epic'),
  item('sunglasses', 'Lunettes', 'uncommon'),
  item('blush', 'Rougissement', 'common'),
  item('demon_eyes', 'Regard démoniaque', 'mythic'),
  item('angel_feathers', 'Plumes', 'epic'),
  item('pirate', 'Pirate', 'rare'),
  item('crown', 'Couronne', 'legendary'),
  item('headphones', 'Casque', 'uncommon'),
  item('mask_oni', 'Masque oni', 'epic'),
  item('butterflies', 'Papillons', 'rare'),
  item('constellation', 'Constellation', 'legendary'),
  item('binary', 'Pluie binaire', 'rare'),
] as const satisfies readonly CosmeticDef[];

export const BANNER_FX = [
  premiumItem('premium_constellation', 'Constellation dorée'),
  item('omnibus', 'Omnibus', 'legendary'),
  item('divine_trio', 'Triade céleste', 'divine'),
  item('trade_baron', 'Baron du commerce', 'legendary'),
  item('summit', 'Sommet', 'mythic'),
  item('team_spirit', 'Esprit d’équipe', 'epic'),
  item('aurora', 'Aurore boréale', 'legendary'),
  item('neon_rain', 'Pluie néon', 'epic'),
  item('great_wave', 'Grande vague', 'epic'),
  item('speed_lines', 'Lignes de vitesse', 'rare'),
  item('halftone', 'Trame', 'uncommon'),
  item('code_rain', 'Pluie de code', 'rare'),
  item('sky_lanterns', 'Lanterne céleste', 'epic'),
  item('nebula', 'Nébuleuse', 'mythic'),
  item('sakura_storm', 'Tempête de cerisiers', 'rare'),
  item('embers_fall', 'Chute de braises', 'rare'),
  item('snow', 'Neige', 'uncommon'),
  item('lightning', 'Éclairs', 'epic'),
  item('film_grain', 'Grain de pellicule', 'uncommon'),
  item('vhs', 'VHS', 'rare'),
  item('gold_dust', 'Poussière d’or', 'legendary'),
  item('ink_wash', 'Lavis d’encre', 'rare'),
  item('constellation', 'Constellation', 'epic'),
  item('grid', 'Grille', 'uncommon'),
  item('spotlight', 'Projecteur', 'rare'),
  item('confetti', 'Confettis', 'common'),
  item('fireflies', 'Lucioles', 'uncommon'),
  item('ocean', 'Océan', 'rare'),
  item('ashen', 'Cendres', 'epic'),
  item('prism', 'Prisme', 'legendary'),
  item('shrine_night', 'Nuit de sanctuaire', 'mythic'),
  secretItem('senita_cosmos', 'Cosmos Senita'),
] as const satisfies readonly CosmeticDef[];

export type TitleKey = (typeof TITLES)[number]['key'];
export type BadgeKey = (typeof BADGES)[number]['key'];
export type FrameKey = (typeof FRAMES)[number]['key'];
export type AvatarFxKey = (typeof AVATAR_FX)[number]['key'];
export type BannerFxKey = (typeof BANNER_FX)[number]['key'];

export const COSMETICS = {
  title: TITLES,
  badge: BADGES,
  frame: FRAMES,
  avatar: AVATAR_FX,
  banner: BANNER_FX,
} as const satisfies Record<UnlockKind, readonly CosmeticDef[]>;

const INDEX = new Map<string, CosmeticDef>(
  (Object.entries(COSMETICS) as Array<[UnlockKind, readonly CosmeticDef[]]>).flatMap(([kind, items]) =>
    items.map((entry) => [`${kind}:${entry.key}`, entry] as const),
  ),
);

export function cosmeticOf(kind: UnlockKind, key: string): CosmeticDef | null {
  return INDEX.get(`${kind}:${key}`) ?? null;
}

export function isKnownCosmetic(kind: UnlockKind, key: string): boolean {
  return INDEX.has(`${kind}:${key}`);
}

export function unlockOf(kind: UnlockKind, key: string): RewardUnlock {
  const entry = cosmeticOf(kind, key);
  if (!entry) throw new Error(`Unknown cosmetic ${kind}:${key}`);
  return { kind, key: entry.key, label: entry.label };
}

export interface EquippedCosmetics {
  title: string | null;
  frame: string | null;
  avatar: string | null;
  banner: string | null;
  badges: string[];
}

export const EMPTY_COSMETICS: EquippedCosmetics = {
  title: null,
  frame: null,
  avatar: null,
  banner: null,
  badges: [],
};

export function parseBadgeKeys(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const value = JSON.parse(raw) as unknown;
    if (!Array.isArray(value)) return [];
    return value.filter((item): item is string => typeof item === 'string').slice(0, MAX_PINNED_BADGES);
  } catch {
    return [];
  }
}
